//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import DescriptionAndValidationWrapper, {
  IDescriptionAndValidationProps,
} from '@components/molecules/description-and-validation-wrapper/DescriptionAndValidationWrapper';
import { useUniqueId } from '@hooks/useUniqueId';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface IOwnProps {
  isDisabled?: boolean;
  label: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  ref?: React.RefObject<HTMLTextAreaElement>;
  resize?: CSSResizeType;
  value?: string;
}
// CSSStyleDeclarations simply defines resize as type string
// these are a subset of the more specific supported values
type CSSResizeType =
  | 'none'
  | 'both'
  | 'horizontal'
  | 'vertical'
  | 'inherit'
  | 'initial';
type TextAreaProps = React.DetailedHTMLProps<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
>;
export type Props = IOwnProps &
  IDescriptionAndValidationProps &
  ITrackableComponent &
  Omit<TextAreaProps, 'onChange' | 'value' | 'ref'>;

/**
 * The `TextAreaInput` atom renders a styled HTML textarea with a text label, and optional
 * error and help text for additional context.
 */
export const TextAreaInput = React.forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      className = '',
      customLabel,
      formComponentId,
      errorText,
      hasError = false,
      helpText,
      isDisabled,
      label,
      labelHelpText,
      onChange = () => undefined,
      resize = 'none',
      rows = 3,
      trackingId,
      value = '',
      labelTextMixin,
      ...textAreaProps
    },
    ref,
  ) => {
    const inputId = useUniqueId('text-area-input-label-id');

    const renderInput = (descriptionTextId?: string) => (
      <TextArea
        {...textAreaProps}
        aria-describedby={descriptionTextId}
        aria-invalid={hasError}
        disabled={isDisabled}
        hasError={hasError}
        id={formComponentId ?? inputId}
        onChange={(e) => onChange(e.target.value)}
        ref={ref}
        resize={resize}
        rows={rows}
        trackingId={trackingId}
        value={value}
      />
    );

    return (
      <DescriptionAndValidationWrapper
        className={className}
        customLabel={customLabel}
        renderFormComponent={renderInput}
        formComponentId={formComponentId ?? inputId}
        errorText={errorText}
        helpText={helpText}
        hasError={hasError}
        label={label}
        labelHelpText={labelHelpText}
        labelTextMixin={labelTextMixin}
      />
    );
  },
);

export const TextArea = withTracking(
  styled.textarea<{ hasError: boolean; resize: CSSResizeType }>(
    ({ hasError, resize, theme }) => css`
      ${hasError ? Mixins.FormControl.Error : Mixins.FormControl.Primary}
      width: 100%;
      height: auto;
      border-radius: ${theme.spacings[1]};
      resize: ${resize};
    `,
  ),
);

TextAreaInput.displayName = 'TextAreaInput';
export default TextAreaInput;
